import { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { FaLinkedin, FaGithub } from 'react-icons/fa';
import { MdEmail } from 'react-icons/md';
import ContactForm from './ContactForm';
import SectionTitle from './SectionTitle';
import { useWeather } from '../context/WeatherContext';

// ─── Option 1: Availability badge ───────────────────────────────────────────
// Uncomment exactly one line (or set to null to hide the badge entirely)
const AVAILABILITY = 'Available for freelance';
// const AVAILABILITY = 'Open to opportunities';
// const AVAILABILITY = 'Actively looking';
// const AVAILABILITY = 'Currently employed · open to chat';
// const AVAILABILITY = 'Not available right now';
// const AVAILABILITY = null;

// ─── Option 2: Skills / what I can help with ────────────────────────────────
// Add, remove, or reorder tags freely
const SKILLS = [
  'Full Stack Dev',
  'MERN Stack',
  'API Integration',
  'UI / UX',
  'Cloud & DevOps',
  'Consulting',
];

// ─── Intent cards — value must match a <option> value in ContactForm ─────────
const INTENT_CARDS = [
  { emoji: '💼', label: 'Hire Me',          value: 'Job Opportunity' },
  { emoji: '🚀', label: 'I Have a Project', value: 'Freelance / Project Inquiry' },
  { emoji: '🤝', label: 'Collaborate',      value: 'Open Source Collaboration' },
  { emoji: '👋', label: 'Just Say Hi',      value: 'Just Saying Hi 👋' },
];

// ─── Animations ──────────────────────────────────────────────────────────────
const pulse = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50%       { opacity: 0.4; transform: scale(1.5); }
`;

const ContactPageStyles = styled.div`
  padding: 10rem 0;
  .contactSection__wrapper {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 4rem;
    align-items: start;
    margin-top: 5rem;
  }
  .left {
    padding: 3rem 2rem 3rem 3rem;
    border-left: 3px solid var(--highlight-blue);
    background: linear-gradient(135deg, rgba(0,123,255,0.04) 0%, transparent 60%);
    border-radius: 0 8px 8px 0;
  }

  /* ── Availability badge ── */
  .left__badge {
    display: inline-flex;
    align-items: center;
    gap: 0.6rem;
    padding: 0.4rem 1rem 0.4rem 0.7rem;
    border-radius: 999px;
    border: 1px solid rgba(34, 197, 94, 0.35);
    background: rgba(34, 197, 94, 0.08);
    font-size: 1.2rem;
    color: #22c55e;
    margin-bottom: 1.6rem;
    letter-spacing: 0.02em;
  }
  .left__badge__dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #22c55e;
    flex-shrink: 0;
    animation: ${pulse} 2s ease-in-out infinite;
  }

  .left__heading {
    font-size: 3.2rem;
    font-family: 'Montserrat SemiBold';
    color: var(--gray-1);
    margin-bottom: 0.8rem;
  }
  .left__subtext {
    font-size: 1.5rem;
    color: var(--gray-1);
    opacity: 0.6;
    margin-bottom: 2.4rem;
    line-height: 1.6;
  }

  /* ── Skills tags ── */
  .left__skills {
    display: flex;
    flex-wrap: wrap;
    gap: 0.8rem;
    margin-bottom: 2.4rem;
  }
  .left__skill {
    padding: 0.35rem 1rem;
    border-radius: 999px;
    border: 1px solid var(--highlight-blue);
    font-size: 1.2rem;
    color: var(--highlight-blue);
    opacity: 0.75;
    letter-spacing: 0.02em;
    white-space: nowrap;
  }

  /* ── Intent cards ── */
  .left__intent-label {
    font-size: 1.2rem;
    color: var(--gray-1);
    opacity: 0.45;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    margin-bottom: 1rem;
  }
  .left__intents {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
    margin-bottom: 0.4rem;
  }
  .left__intent {
    display: flex;
    align-items: center;
    gap: 0.8rem;
    padding: 1rem 1.2rem;
    border-radius: 10px;
    border: 1px solid var(--gray-2);
    background: transparent;
    color: var(--gray-1);
    font-size: 1.35rem;
    font-family: 'RobotoMono Regular', monospace;
    cursor: pointer;
    transition: border-color 0.2s ease, background 0.2s ease, color 0.2s ease;
    text-align: left;
    .intent__emoji { font-size: 1.6rem; line-height: 1; }
    &:hover {
      border-color: var(--highlight-blue);
      color: var(--highlight-blue);
    }
    &.active {
      border-color: var(--highlight-blue);
      background: rgba(0, 123, 255, 0.08);
      color: var(--highlight-blue);
    }
  }

  .left__visitor {
    margin-top: 1.6rem;
    font-size: 1.25rem;
    font-style: italic;
    color: var(--gray-1);
    opacity: 0.5;
    line-height: 1.5;
  }
  .left__social {
    display: flex;
    gap: 1.6rem;
    margin-top: 2.8rem;
  }
  .left__social a {
    display: flex;
    align-items: center;
    gap: 0.8rem;
    font-size: 1.4rem;
    color: var(--gray-1);
    opacity: 0.7;
    transition: opacity 0.2s ease, color 0.2s ease;
    svg {
      width: 2rem;
      height: 2rem;
    }
    &:hover {
      opacity: 1;
      color: var(--highlight-blue);
    }
  }
  .right {
    background: var(--deep-dark);
    padding: 3.5rem 3rem 3rem;
    border-radius: 12px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  }
  @media only screen and (max-width: 768px) {
    .contactSection__wrapper {
      grid-template-columns: 1fr;
      gap: 3rem;
    }
    .left {
      padding: 1.5rem 1.5rem 1.5rem 2rem;
    }
    .left__intents {
      grid-template-columns: 1fr 1fr;
    }
  }
`;

export default function ContactSection() {
  const weather = useWeather();
  const visitorCity = weather?.city && weather.city !== 'Toronto' ? weather.city : null;
  const [reason, setReason] = useState('');

  return (
    <ContactPageStyles>
      <div className="container">
        <SectionTitle heading="Contact Me" subheading="We'd love to hear from you" />
        <div className="contactSection__wrapper">
          <div className="left">

            {AVAILABILITY && (
              <div className="left__badge">
                <span className="left__badge__dot" />
                {AVAILABILITY}
              </div>
            )}

            <h2 className="left__heading">Let's talk</h2>
            <p className="left__subtext">
              Have a project in mind or just want to connect?<br />
              I typically respond within 24 hours.
            </p>

            <div className="left__skills">
              {SKILLS.map((skill) => (
                <span key={skill} className="left__skill">{skill}</span>
              ))}
            </div>

            <p className="left__intent-label">What brings you here?</p>
            <div className="left__intents">
              {INTENT_CARDS.map((card) => (
                <button
                  key={card.value}
                  type="button"
                  className={`left__intent${reason === card.value ? ' active' : ''}`}
                  onClick={() => setReason(reason === card.value ? '' : card.value)}
                >
                  <span className="intent__emoji">{card.emoji}</span>
                  {card.label}
                </button>
              ))}
            </div>

            {visitorCity && (
              <p className="left__visitor">
                Reaching out from {visitorCity}? I&apos;m just over in Toronto — let&apos;s connect.
              </p>
            )}

            <div className="left__social">
              <a href="mailto:ianasqazi@gmail.com">
                <MdEmail /> Email
              </a>
              <a href="https://www.linkedin.com/in/anasqazi/" target="_blank" rel="noopener noreferrer">
                <FaLinkedin /> LinkedIn
              </a>
              <a href="https://github.com/ianasqazi" target="_blank" rel="noopener noreferrer">
                <FaGithub /> GitHub
              </a>
            </div>
          </div>
          <div className="right">
            <ContactForm reason={reason} onReasonChange={setReason} />
          </div>
        </div>
      </div>
    </ContactPageStyles>
  );
}
